import React from 'react'
import { Container, Card } from 'react-bootstrap'
import * as S from './pdfElements'
import { Image } from 'Maxxia/images'
import {
  capitalizeOnlyFirstLetter,
  formatCurrency,
  formatNumber,
  payFrequencyToPaysPA,
  toLowerCase,
} from 'application/helpers'
import { IVehicleProps } from 'application/utils/type'
import { useLocation } from 'react-router-dom'

interface ISummarySheetMXProps {
    summarySheetData: {
        accessories: string[]
        annualRate: number
        carRepaymentPerAnnumExGST: number
        carRepaymentPerAnnumIncGST: number
        carRepaymentPerPayExGST: number
        carRepaymentPerPayIncGST: number
        compInsPerAnnumExGST: number
        compInsPerAnnumIncGST: number
        compInsPerPayExGST: number
        compInsPerPayIncGST: number
        electricityPerAnnumExGST: number
        electricityPerAnnumIncGST: number
        electricityPerPayExGST: number
        electricityPerPayIncGST: number
        employeeName: string
        employeeNumber: string
        financeAmount: number
        fuelCards: string[]
        fuelPerAnnumExGST: number
        fuelPerAnnumIncGST: number
        fuelPerPayExGST: number
        fuelPerPayIncGST: number
        fuelType: string
        grossSalaryPerPay: number
        gstCreditClaimable: number
        gstOnECMPerAnnum: number
        gstSavingsOnRunningCosts: number
        incidentalsPerAnnumExGST: number
        incidentalsPerAnnumIncGST: number
        incidentalsPerPayExGST: number
        incidentalsPerPayIncGST: number
        isExGST: boolean
        kmPerAnnum: number
        leaseResidualAmountIncGST: number
        leaseTerm: number 
        maintenancePerAnnumExGST: number
        maintenancePerAnnumIncGST: number
        maintenancePerPayExGST: number
        maintenancePerPayIncGST: number
        netIncomePerPay: number
        netSalaryPerPay: number
        nlFeePerAnnumExGST: number
        nlFeePerAnnumIncGST: number
        noPkgCashInHand1: number
        noPkgNetSalary: number
        noPkgTaxAndMedicare: number
        outOfPocketPerPay: number
        payFrequency: string
        postTaxDeduction: number
        postTaxPerPay: number
        preTaxPerPay: number
        priveawayPrice:number
        productId: string
        regoPerAnnumExGST: number
        regoPerAnnumIncGST: number
        regoPerPayExGST: number
        regoPerPayIncGST: number
        roadsidePerAnnumExGST: number
        roadsidePerAnnumIncGST: number
        roadsidePerPayExGST: number
        roadsidePerPayIncGST: number
        savingsPerPay: number
        spFeePerAnnumExGST: number
        spFeePerAnnumIncGST: number
        takeHomePerPay: number
        taxMedicarePerPay: number
        tyresPerAnnumExGST: number
        tyresPerAnnumIncGST: number
        tyresPerPayExGST: number
        tyresPerPayIncGST: number
        vehicle: IVehicleProps
        vehicleFBTValue: number
    }
}

export const SummarySheetMX = (props: ISummarySheetMXProps) => {
    const location = useLocation()
    const proposalIndex: number = location.pathname.lastIndexOf('/')
    const result:string = location.pathname.substring(proposalIndex + 1)
    const isPDF: boolean =  result === 'pdfview'
    const paysPA: number = payFrequencyToPaysPA(props.summarySheetData.payFrequency)
    let vehicleOptions = props.summarySheetData.accessories.join("; ").trim() !== ";" ? props.summarySheetData.accessories.join("; ") : "No vehicle options confirmed"
    let vehicleDesc = props.summarySheetData.vehicle.id !== null ? props.summarySheetData.vehicle.vehicleDescription : 'Not specified'
    
    let fuelConsumption = 'To be confirmed'
    if(props.summarySheetData.vehicle.id) {
         if(props.summarySheetData.vehicle.fuelType)
            fuelConsumption = "(" + capitalizeOnlyFirstLetter(props.summarySheetData.vehicle.fuelType) + ") "
        if(props.summarySheetData.vehicle.fuelConsumption)
            fuelConsumption += props.summarySheetData.vehicle.fuelConsumption.toFixed(1)
    }

    let electricityConsumption = 'To be confirmed'
    if(toLowerCase(props.summarySheetData.vehicle.fuelType) === 'electric' && props.summarySheetData.vehicle.kwhper100km)
        electricityConsumption = props.summarySheetData.vehicle.kwhper100km.toFixed(1)

    const runningCostsSubtotal = (numPayments: number) => {
        
        let total: number = 0
        if(props.summarySheetData.isExGST)
            total = roundTo2(props.summarySheetData.carRepaymentPerAnnumExGST / numPayments) +
                    roundTo2(props.summarySheetData.nlFeePerAnnumExGST / numPayments) +
                    roundTo2(props.summarySheetData.spFeePerAnnumExGST / numPayments) +
                    roundTo2(props.summarySheetData.maintenancePerAnnumExGST / numPayments) +
                    roundTo2(props.summarySheetData.tyresPerAnnumExGST / numPayments) +
                    roundTo2(props.summarySheetData.fuelPerAnnumExGST / numPayments) +
                    roundTo2(props.summarySheetData.electricityPerAnnumExGST / numPayments) +
                    roundTo2(props.summarySheetData.regoPerAnnumExGST / numPayments) +
                    roundTo2(props.summarySheetData.incidentalsPerAnnumExGST / numPayments) +
                    roundTo2(props.summarySheetData.compInsPerAnnumExGST / numPayments) + 
                    roundTo2(props.summarySheetData.roadsidePerAnnumExGST / numPayments) +
                    roundTo2(props.summarySheetData.gstOnECMPerAnnum / numPayments)
        else
            total = roundTo2(props.summarySheetData.carRepaymentPerAnnumIncGST / numPayments) +
                    roundTo2(props.summarySheetData.nlFeePerAnnumIncGST / numPayments) +
                    roundTo2(props.summarySheetData.spFeePerAnnumIncGST / numPayments) +
                    roundTo2(props.summarySheetData.maintenancePerAnnumIncGST / numPayments) +
                    roundTo2(props.summarySheetData.tyresPerAnnumIncGST / numPayments) +
                    roundTo2(props.summarySheetData.fuelPerAnnumIncGST / numPayments) +
                    roundTo2(props.summarySheetData.electricityPerAnnumIncGST / numPayments) +
                    roundTo2(props.summarySheetData.regoPerAnnumIncGST / numPayments) +
                    roundTo2(props.summarySheetData.incidentalsPerAnnumIncGST / numPayments) +
                    roundTo2(props.summarySheetData.compInsPerAnnumIncGST / numPayments) +
                    roundTo2(props.summarySheetData.roadsidePerAnnumIncGST / numPayments) 

        return total
    }

    const roundTo2 = (value: number) => {
        return Math.round(value * 100) / 100
    }

    let gstSavingsOnRunningCosts: number = 0
    if(props.summarySheetData.isExGST) {
        let gstAnnualTotal = (props.summarySheetData.carRepaymentPerAnnumIncGST / 11) +
                    (props.summarySheetData.nlFeePerAnnumIncGST / 11) +
                    (props.summarySheetData.spFeePerAnnumIncGST / 11) +
                    (props.summarySheetData.maintenancePerAnnumIncGST / 11) +
                    (props.summarySheetData.tyresPerAnnumIncGST / 11) +
                    (props.summarySheetData.fuelPerAnnumIncGST / 11) +
                    (props.summarySheetData.electricityPerAnnumIncGST / 11) +
                    (props.summarySheetData.incidentalsPerAnnumIncGST / 11) +
                    (props.summarySheetData.roadsidePerAnnumIncGST / 11) +
                    (props.summarySheetData.compInsPerAnnumIncGST / 11)

        gstSavingsOnRunningCosts = (gstAnnualTotal - props.summarySheetData.gstOnECMPerAnnum) / 12 * props.summarySheetData.leaseTerm //Convert annual to total over term of lease
    }
    else
         gstSavingsOnRunningCosts = props.summarySheetData.gstSavingsOnRunningCosts

    const fuelCard = () => {
        if(props.summarySheetData.electricityPerAnnumIncGST > 0 && props.summarySheetData.fuelPerAnnumIncGST === 0)
                return "Not available for Electric Vehicles"

        let tmpCards = []
        tmpCards.push(...props.summarySheetData.fuelCards)
        let last
        if (tmpCards.length > 0) {
            last = tmpCards.pop();
        
            if(tmpCards.length > 0) {
                return tmpCards.join(', ') + ' and ' + last + ' fuel cards';
            }
            else 
                return last + ' fuel card'
        }
        else {
            return 'Choice of BP, Shell and Ampol available'
        }
    }

    const NormalHeader = () => {
        return (
            <S.MXSummaryHeader>
                <Image imageName={'LogoNormal'} height={35} style={{marginRight:'auto', marginLeft:'9px'}}/>
                <span style={{fontWeight:'900'}}>QUOTE SUMMARY TABLE</span>
                <span style={{marginLeft:'auto'}} />
            </S.MXSummaryHeader>
        )
    }

    const GeneralDetails = () => {
        return (
            <S.MXSummaryTable>
                <tbody>
                    <tr>
                        <S.BoldTH colSpan={2}>Employee details</S.BoldTH>
                    </tr>
                    <tr>
                        <td style={{width:'55%'}}>Employee name:</td>
                        <td>{props.summarySheetData.employeeName}</td>
                    </tr>
                    <tr>
                        <td>Employee number:</td>
                        <td>{props.summarySheetData.employeeNumber ? props.summarySheetData.employeeNumber : 'Not yet provided'}</td>
                    </tr>
                    <tr>
                        <td>Vehicle description:</td>
                        <td>{vehicleDesc}</td>
                    </tr>
                    <tr>
                        <td>Vehicle options:</td>
                        <td>{vehicleOptions}</td>
                    </tr>
                    <tr>
                        <td>Price of vehicle (Incl GST):</td>
                        <td>{formatCurrency(props.summarySheetData.priveawayPrice + props.summarySheetData.gstCreditClaimable)}</td>
                    </tr>
                    <tr>
                        <td>Novated lease included services:</td>
                        <td>{props.summarySheetData.productId}</td>
                    </tr>

                    <tr >
                        <S.BoldTH style={{paddingTop: '18px'}} colSpan={2}>Summary of estimated savings over the lease term</S.BoldTH>
                    </tr>
                    <tr>
                        <td>Savings on vehicle purchase (GST Savings on Purchase Price):</td>
                        <td>{formatCurrency(props.summarySheetData.gstCreditClaimable)}</td>
                    </tr>
                    <tr>
                        <td>Estimated GST savings on running costs:</td>
                        <td>{formatCurrency(gstSavingsOnRunningCosts)}</td>
                    </tr>
                    <tr>
                        <td>Estimated income tax savings:</td>
                        <td>{formatCurrency(props.summarySheetData.savingsPerPay * paysPA / 12 * props.summarySheetData.leaseTerm)}</td>
                    </tr>
                    <tr >
                        <S.BoldTH style={{paddingTop: '18px'}} colSpan={2}>Lease details</S.BoldTH>
                    </tr>
                    <tr>
                        <td>Lease term - months:</td>
                        <td>{props.summarySheetData.leaseTerm}</td>
                    </tr>
                    <tr>
                        <td>Annual kilometers:</td>
                        <td>{formatNumber(props.summarySheetData.kmPerAnnum)}</td>
                    </tr>
                    {toLowerCase(props.summarySheetData.vehicle.fuelType) !== 'electric' && 
                        <tr>
                            <td>Fuel - Litres per 100km:</td>
                            <td>{fuelConsumption}</td>
                        </tr>
                    }
                    {toLowerCase(props.summarySheetData.vehicle.fuelType) === 'electric' && 
                        <tr>
                            <td>Electricity - kWh per 100km:</td>
                            <td>{electricityConsumption}</td>
                        </tr>
                    }
                    <tr>
                        <td>Fuel cards:</td>
                        <td>{fuelCard()}</td>
                    </tr>
                    <tr>
                        <td>Vehicle amount financed:</td>
                        <td>{formatCurrency(props.summarySheetData.financeAmount)}</td>
                    </tr>
                    <tr>
                        <td>Finance interest rate:</td>
                        <td>{props.summarySheetData.annualRate.toFixed(2)}%</td>
                    </tr>
                    <tr>
                        <td>Comparative rate:</td>
                        <td>{props.summarySheetData.annualRate.toFixed(2)}%</td>
                    </tr>
                    <tr>
                        <td>Residual value (Incl GST):</td>
                        <td>{formatCurrency(props.summarySheetData.leaseResidualAmountIncGST)}</td>
                    </tr>
                    <tr>
                        <td>FBT statutory %:</td>
                        <td>20%</td>
                    </tr>
                    <tr>
                        <td>Vehicle FBT base value:</td>
                        <td> {formatCurrency(props.summarySheetData.vehicleFBTValue)}</td>
                    </tr>
                </tbody>
            </S.MXSummaryTable>
        )
    }
    
    const Calculations = () => {
        return (

                <S.MXSummaryTable>
                <tbody>
                    <tr>
                        <S.BoldTH style={{paddingTop: '18px'}} colSpan={4}>Calculations</S.BoldTH>
                    </tr>
                    <tr>
                        <th style={{width:'40%'}}>Item</th>
                        <th style={{width:'20%'}}>Annual</th>
                        <th style={{width:'20%'}}>Month</th>
                        <th style={{width:'20%'}}>Per Pay</th>
                    </tr>
                    <tr>
                        <td>Finance charges (Lease):</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.carRepaymentPerAnnumExGST : props.summarySheetData.carRepaymentPerAnnumIncGST)}</td>
                        <td>{formatCurrency((props.summarySheetData.isExGST ? props.summarySheetData.carRepaymentPerAnnumExGST : props.summarySheetData.carRepaymentPerAnnumIncGST)/12)}</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.carRepaymentPerPayExGST : props.summarySheetData.carRepaymentPerPayIncGST)}</td>
                    </tr>
                    <tr>
                        <td>Management fee:</td>
                        <td>$0</td>
                        <td>$0</td>
                        <td>$0</td>
                    </tr>
                    <tr>
                        <td>Other fees:</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? (props.summarySheetData.nlFeePerAnnumExGST + props.summarySheetData.spFeePerAnnumExGST ) : (props.summarySheetData.nlFeePerAnnumIncGST + props.summarySheetData.spFeePerAnnumIncGST))}</td>
                        <td>{formatCurrency((props.summarySheetData.isExGST ? (props.summarySheetData.nlFeePerAnnumExGST + props.summarySheetData.spFeePerAnnumExGST) : (props.summarySheetData.nlFeePerAnnumIncGST + props.summarySheetData.spFeePerAnnumIncGST))/12)}</td>
                        <td>{formatCurrency((props.summarySheetData.isExGST ? (props.summarySheetData.nlFeePerAnnumExGST + props.summarySheetData.spFeePerAnnumExGST) : (props.summarySheetData.nlFeePerAnnumIncGST + props.summarySheetData.spFeePerAnnumIncGST))/paysPA)}</td>
                    </tr>
                    <tr>
                        <td>Maintenance:</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.maintenancePerAnnumExGST : props.summarySheetData.maintenancePerAnnumIncGST)}</td>
                        <td>{formatCurrency((props.summarySheetData.isExGST ? props.summarySheetData.maintenancePerAnnumExGST : props.summarySheetData.maintenancePerAnnumIncGST)/12)}</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.maintenancePerPayExGST : props.summarySheetData.maintenancePerPayIncGST)}</td>
                    </tr>
                    <tr>
                        <td>Tyres:</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.tyresPerAnnumExGST : props.summarySheetData.tyresPerAnnumIncGST)}</td>
                        <td>{formatCurrency((props.summarySheetData.isExGST ? props.summarySheetData.tyresPerAnnumExGST : props.summarySheetData.tyresPerAnnumIncGST)/12)}</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.tyresPerPayExGST : props.summarySheetData.tyresPerPayIncGST)}</td>
                    </tr>
                    {props.summarySheetData.fuelPerAnnumExGST > 0 &&
                        <tr>
                            <td>Fuel:</td>
                            <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.fuelPerAnnumExGST : props.summarySheetData.fuelPerAnnumIncGST)}</td>
                            <td>{formatCurrency((props.summarySheetData.isExGST ? props.summarySheetData.fuelPerAnnumExGST : props.summarySheetData.fuelPerAnnumIncGST)/12)}</td>
                            <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.fuelPerPayExGST : props.summarySheetData.fuelPerPayIncGST)}</td>
                        </tr>
                    }
                    {props.summarySheetData.electricityPerAnnumIncGST > 0 && 
                        <tr>
                            <td>Electricity:</td>
                            <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.electricityPerAnnumExGST : props.summarySheetData.electricityPerAnnumIncGST)}</td>
                            <td>{formatCurrency((props.summarySheetData.isExGST ? props.summarySheetData.electricityPerAnnumExGST : props.summarySheetData.electricityPerAnnumIncGST)/12)}</td>
                            <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.electricityPerPayExGST : props.summarySheetData.electricityPerPayIncGST)}</td>
                        </tr>
                    }
                    <tr>
                        <td>Registration:</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.regoPerAnnumExGST : props.summarySheetData.regoPerAnnumIncGST)}</td>
                        <td>{formatCurrency((props.summarySheetData.isExGST ? props.summarySheetData.regoPerAnnumExGST : props.summarySheetData.regoPerAnnumIncGST)/12)}</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.regoPerPayExGST : props.summarySheetData.regoPerPayIncGST)}</td>
                    </tr>
                    <tr>
                        <td>Incidentals:</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.incidentalsPerAnnumExGST : props.summarySheetData.incidentalsPerAnnumIncGST)}</td>
                        <td>{formatCurrency((props.summarySheetData.isExGST ? props.summarySheetData.incidentalsPerAnnumExGST : props.summarySheetData.incidentalsPerAnnumIncGST)/12)}</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.incidentalsPerPayExGST : props.summarySheetData.incidentalsPerPayIncGST)}</td>
                    </tr>
                    <tr>
                        <td>Comprehensive insurance:</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.compInsPerAnnumExGST : props.summarySheetData.compInsPerAnnumIncGST)}</td>
                        <td>{formatCurrency((props.summarySheetData.isExGST ? props.summarySheetData.compInsPerAnnumExGST : props.summarySheetData.compInsPerAnnumIncGST)/12)}</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.compInsPerPayExGST : props.summarySheetData.compInsPerPayIncGST)}</td>
                    </tr>
                    <tr>
                        <td>Roadside assistance:</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.roadsidePerAnnumExGST : props.summarySheetData.roadsidePerAnnumIncGST)}</td>
                        <td>{formatCurrency((props.summarySheetData.isExGST ? props.summarySheetData.roadsidePerAnnumExGST : props.summarySheetData.roadsidePerAnnumIncGST)/12)}</td>
                        <td>{formatCurrency(props.summarySheetData.isExGST ? props.summarySheetData.roadsidePerPayExGST : props.summarySheetData.roadsidePerPayIncGST)}</td>
                    </tr>
                    {props.summarySheetData.isExGST &&
                        <tr>
                            <td>GST on ECM</td>
                            <td>{formatCurrency(props.summarySheetData.gstOnECMPerAnnum)}</td>
                            <td>{formatCurrency(props.summarySheetData.gstOnECMPerAnnum / 12)}</td>
                            <td>{formatCurrency(props.summarySheetData.gstOnECMPerAnnum / paysPA)}</td>
                        </tr>
                    }   
                    <tr style={{backgroundColor: '#e6e6e6'}}>
                        <td style={{fontWeight: '900'}}>Running costs subtotal:</td>
                        <td>{formatCurrency(runningCostsSubtotal(1))}</td>
                        <td>{formatCurrency(runningCostsSubtotal(12))}</td>
                        <td>{formatCurrency(runningCostsSubtotal(paysPA))}</td>
                    </tr>
                    <tr>
                        <td>Less estimated tax reduction:</td>
                        <td>{formatCurrency(props.summarySheetData.savingsPerPay * paysPA)}</td>
                        <td>{formatCurrency(props.summarySheetData.savingsPerPay * paysPA / 12)}</td>
                        <td>{formatCurrency(props.summarySheetData.savingsPerPay)}</td>
                    </tr>
                    <tr style={{backgroundColor: '#e6e6e6'}}>
                        <td style={{fontWeight: '900'}}>Estimated cost on take home pay:</td>
                        <td>{formatCurrency(runningCostsSubtotal(1) - (props.summarySheetData.savingsPerPay * paysPA))}</td>
                        <td>{formatCurrency((runningCostsSubtotal(12)) - props.summarySheetData.savingsPerPay * paysPA / 12)}</td>
                        <td>{formatCurrency((runningCostsSubtotal(paysPA)) - props.summarySheetData.savingsPerPay)}</td>
                    </tr>
                </tbody>
            </S.MXSummaryTable>
        )
    }

    const EstImpactOnSalary = () => {
        return (
            <S.MXSummaryTable>
                <tbody>
                    <tr>
                        <S.BoldTH style={{paddingTop: '18px'}} colSpan={3}>Estimated impact on salary</S.BoldTH>
                    </tr>
                    <tr>
                        <th style={{width:'50%'}}></th>
                        <th style={{width:'25%'}}>Current pay</th>
                        <th style={{width:'25%'}}>Pay post novated lease</th>
                    </tr>
                    <tr>
                        <td>{props.summarySheetData.payFrequency + '  taxable salary:'}</td>
                        <td>{formatCurrency(props.summarySheetData.grossSalaryPerPay)}</td>
                        <td>{formatCurrency(props.summarySheetData.grossSalaryPerPay)}</td>
                    </tr>
                    <tr>
                        <td>Pre-tax deduction (Operating expenses of vehicle):</td>
                        <td>$0.00</td>
                        <td>{formatCurrency(props.summarySheetData.preTaxPerPay)}</td>
                    </tr>
                    <tr>
                        <td>PAYG:</td>
                        <td>{formatCurrency(props.summarySheetData.noPkgTaxAndMedicare/paysPA)}</td>
                        <td>{formatCurrency(props.summarySheetData.taxMedicarePerPay)}</td>
                    </tr>
                    <tr>
                        <td>Net income:</td>
                        <td>{formatCurrency(props.summarySheetData.noPkgNetSalary/paysPA)}</td>
                        <td>{formatCurrency(props.summarySheetData.netIncomePerPay)}</td>
                    </tr>
                    <tr>
                        <td>Post-tax deduction:</td>
                        <td>$0.00</td>
                        <td>{formatCurrency(props.summarySheetData.postTaxPerPay)}</td>
                    </tr>
                    <tr>
                        <td>Take home:</td>
                        <td>{formatCurrency(props.summarySheetData.noPkgCashInHand1/paysPA)}</td>
                        <td>{formatCurrency(props.summarySheetData.takeHomePerPay)}</td>
                    </tr>
                </tbody>
            </S.MXSummaryTable>)
    }

    return (
        <>
            {!isPDF && 
                <>
                    <NormalHeader />
                
                    <S.SummarySheet>
                        <GeneralDetails />

                        <Calculations />
                    
                        <EstImpactOnSalary />
                                                
                    </S.SummarySheet>
                </>
            }
            {isPDF && 
                <>
                    <div className="card-section">
                        <Container className="section mt-4" id="cardetailssection">
                        <S.StyledCard>
                            <S.StyledCardHeader>
                            <S.StyledCardHeaderText id="cardetailssection.title">
                                Quote Summary Table
                            </S.StyledCardHeaderText>
                            </S.StyledCardHeader>
                            <Card.Body>
                            <table>
                                <GeneralDetails />
                            </table>
                            </Card.Body>
                        </S.StyledCard>
                        </Container>
                        <Container className="section mt-4" id="cardetailssection">
                            <S.StyledCard>
                                <S.StyledCardHeader>
                                <S.StyledCardHeaderText id="cardetailssection.title">
                                    Quote Summary Table (cont)
                                </S.StyledCardHeaderText>
                                </S.StyledCardHeader>
                                <Card.Body>
                                
                                    <Calculations />
                                    <EstImpactOnSalary />
                                
                                </Card.Body>
                            </S.StyledCard>
                        </Container>
                    </div>
                </>
            }
        </>
    )
}
